'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { DotFlow } from '../ui/dot-flow'
import { AnimatedBiomoLogo } from '../ui/animated-biomo-logo'
import { trackCtaClick } from '@/lib/analytics/rastreamento'

const dotFlowFrames = {
  loading: [
    [0, 1, 2], [3, 4, 5], [6, 7, 8],
    [0, 3, 6], [1, 4, 7], [2, 5, 8],
    [0, 4, 8], [2, 4, 6],
  ],
  pulse: [
    [4], [1, 3, 4, 5, 7],
    [0, 1, 2, 3, 4, 5, 6, 7, 8],
    [1, 3, 4, 5, 7], [4],
  ],
  check: [
    [6], [4, 6], [2, 4, 6], [2, 4], [2, 4, 6],
  ],
}

const headerDotFlowItems = [
  { title: "Quero meu site", frames: dotFlowFrames.pulse, duration: 150, repeatCount: 4 },
  { title: "Começar agora", frames: dotFlowFrames.loading, duration: 120, repeatCount: 3 },
  { title: "Fazer orçamento", frames: dotFlowFrames.check, duration: 180, repeatCount: 3 },
]

const navLinks = [
  { href: '#portfolio', label: 'Portfólio' },
  { href: '#como-funciona', label: 'Como Funciona' },
  { href: '#depoimentos', label: 'Depoimentos' },
]

interface LiquidGlassHeaderProps {
  onStart: () => void
}

export function LiquidGlassHeader({ onStart }: LiquidGlassHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20)
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    const el = document.querySelector(href)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100]"
      style={{
        padding: isScrolled ? '8px 8px 0' : '0',
        transition: 'padding 500ms cubic-bezier(0.25, 0.1, 0.25, 1)',
      }}
    >
      <header
        className={isScrolled ? 'liquid-glass-header' : ''}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isScrolled ? '12px 20px' : '12px 16px',
          borderRadius: isScrolled ? '1.5rem' : '0',
          backgroundColor: isScrolled
            ? 'var(--header-glass-bg)'
            : 'transparent',
          backdropFilter: isScrolled ? 'blur(var(--header-blur))' : 'none',
          WebkitBackdropFilter: isScrolled ? 'blur(var(--header-blur))' : 'none',
          borderWidth: isScrolled ? '1px' : '0px',
          borderStyle: 'solid',
          borderColor: isScrolled
            ? 'var(--header-border-color)'
            : 'transparent',
          boxShadow: isScrolled
            ? '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -4px rgba(0,0,0,0.05)'
            : 'none',
          transition: 'all 500ms cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}
      >
        {/* Logo */}
        <a href="/" aria-label="Ir para página inicial" className="flex items-center gap-2 shrink-0 group">
          <AnimatedBiomoLogo className="h-8 w-[29px] sm:h-9 sm:w-[33px] transition-transform group-hover:scale-105" />
          <span className="text-indigo-950 dark:text-white font-semibold text-xl tracking-tight">Biomo</span>
        </a>

        <div className="flex items-center gap-3 sm:gap-8">
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-medium text-indigo-950/80 dark:text-white/70 hover:text-purple-500 dark:hover:text-purple-400 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* DotFlow CTA */}
          <button onClick={() => { trackCtaClick('Fazer orçamento', 'header'); onStart() }} aria-label="Fazer orçamento" className="hover:scale-105 active:scale-95 transition-transform">
            <DotFlow items={headerDotFlowItems} variant="purple" minWidth="90px" />
          </button>
        </div>
      </header>
    </div>
  )
}
